import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import './DocMediaTimePicker.scss';
import {Box} from '@mui/material';
import {t} from 'i18next';

type ValidationError = {minutes?: boolean; seconds?: boolean};

interface DocMediaTimePickerProps {
  initialValue?: string;
  onChange?: (
    value: string,
    context: {isValid: boolean; errors: ValidationError}
  ) => void;
  className?: string;
}

/* Utils */
const pad2 = (n: string | number) => String(n ?? '').padStart(2, '0');
const onlyDigits = (s: string) => s.replaceAll(/\D+/g, '');

const clampMS = (val: string) => {
  let d = onlyDigits(val).slice(0, 2);
  if (d.length === 1 && Number(d[0]) > 5) d = '5';
  if (d.length === 2 && Number(d) > 59) d = '59';
  return d;
};

const parseInitial = (val?: string) => {
  if (!val) return {h: '0', m: '00', s: '00'};
  const parts = val.split(':');
  const hRaw = onlyDigits(parts[0] ?? '0').slice(0, 100);
  const h = hRaw || '0';
  const m = pad2(clampMS(parts[1] ?? '00')) || '00';
  const s = pad2(clampMS(parts[2] ?? '00')) || '00';
  return {h, m, s};
};

export const DocMediaTimePicker: React.FC<DocMediaTimePickerProps> = ({
  initialValue,
  onChange,
  className,
}: DocMediaTimePickerProps) => {
  const init = useMemo(() => parseInitial(initialValue), [initialValue]);

  const [hours, setHours] = useState(init.h);
  const [minutes, setMinutes] = useState(init.m);
  const [seconds, setSeconds] = useState(init.s);

  const [minErr, setMinErr] = useState(false);
  const [secErr, setSecErr] = useState(false);

  const hRef = useRef<HTMLInputElement | null>(null);
  const minRef = useRef<HTMLInputElement | null>(null);
  const secRef = useRef<HTMLInputElement | null>(null);

  const containerClass = useMemo(
    () =>
      ['doc-media-time-field transparent', className].filter(Boolean).join(' '),
    [className]
  );

  const emit = useCallback(
    (h = hours, m = minutes, s = seconds) => {
      const hOut = h || '0';
      const v = `${hOut}:${pad2(m)}:${pad2(s)}`;
      const errors: ValidationError = {minutes: minErr, seconds: secErr};
      onChange?.(v, {isValid: !minErr && !secErr, errors});
    },
    [hours, minErr, minutes, onChange, secErr, seconds]
  );

  useEffect(() => {
    emit();
  }, [hours, minutes, seconds, minErr, secErr]);

  const clampMSInput = useCallback((raw: string) => {
    let d = onlyDigits(raw).slice(0, 2);
    if (d.length === 1 && Number(d[0]) > 5) d = '5';
    if (d.length === 2 && Number(d) > 59) d = '59';
    return d;
  }, []);

  const handleHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = onlyDigits(e.target.value).slice(0, 100);
      const updatedValue = val.replace(/^0+(?!$)/, '');
      setHours(updatedValue);
    },
    []
  );

  const handleMinutesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = clampMSInput(e.target.value);
      setMinutes(d);
      setMinErr(false);
    },
    [clampMSInput]
  );

  const handleSecondsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const d = clampMSInput(e.target.value);
      setSeconds(d);
      setSecErr(false);
    },
    [clampMSInput]
  );

  const padOnBlur = useCallback((part: 'h' | 'm' | 's') => {
    if (part === 'h') setHours((v) => (v === '' ? '0' : v));
    if (part === 'm') setMinutes((v) => pad2(v === '' ? '00' : v));
    if (part === 's') setSeconds((v) => pad2(v === '' ? '00' : v));
  }, []);

  const onHoursKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ':' || e.key === 'Enter') {
        e.preventDefault();
        minRef.current?.focus();
        minRef.current?.select();
      }
    },
    []
  );

  const onMinutesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ':' || e.key === 'Enter') {
        e.preventDefault();
        secRef.current?.focus();
        secRef.current?.select();
      }
    },
    []
  );

  const onContainerClick = useCallback(() => {
    hRef.current?.focus();
    hRef.current?.select();
  }, []);

  const hoursWidthCh = useMemo(
    () => Math.max(1, hours.length || 1),
    [hours.length]
  );

  const selectInput = useCallback(
    (ref: React.RefObject<HTMLInputElement | null>) =>
      (e: React.MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        ref.current?.focus();
        ref.current?.select();
      },
    []
  );

  return (
    <Box
      component={'div'}
      data-testid="doc-media-time-picker"
      className={containerClass}
      aria-label={t('docRemarks.timerInput.timeInput')}
      onClick={onContainerClick}
    >
      <input
        id="dm-h"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={100000}
        value={hours}
        autoComplete={'off'}
        onChange={handleHoursChange}
        onPaste={(e) => e.preventDefault()}
        onBlur={() => padOnBlur('h')}
        onKeyDown={onHoursKeyDown}
        onClick={selectInput(hRef)}
        aria-label={t('docRemarks.timerInput.hours')}
        placeholder="0"
        className="doc-media-time-input doc-media-time-input--hours"
        style={{width: `${hoursWidthCh}.7ch`}}
        ref={hRef}
        data-testid="doc-media-time-picker-hours" />
      <span className="doc-media-time-sep" data-testid={`doc-media-time-picker-`}>:</span>
      <input
        id="dm-m"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={minutes}
        autoComplete={'off'}
        onPaste={(e) => e.preventDefault()}
        onChange={handleMinutesChange}
        onBlur={() => padOnBlur('m')}
        onKeyDown={onMinutesKeyDown}
        onClick={selectInput(minRef)}
        aria-invalid={minErr || undefined}
        aria-describedby={minErr ? 'dm-m-err' : undefined}
        aria-label={t('docRemarks.timerInput.minutes')}
        placeholder="00"
        className={`doc-media-time-input doc-media-time-input--minutes`}
        style={{width: `2.7ch`}}
        ref={minRef}
        data-testid="doc-media-time-picker-minutes" />
      <span
        className="doc-media-time-sep"
        data-testid={`doc-media-time-picker--text`}>:</span>
      <input
        id="dm-s"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={seconds}
        autoComplete={'off'}
        onPaste={(e) => e.preventDefault()}
        onChange={handleSecondsChange}
        onBlur={() => padOnBlur('s')}
        onClick={selectInput(secRef)}
        aria-invalid={secErr || undefined}
        aria-describedby={secErr ? 'dm-s-err' : undefined}
        aria-label={t('docRemarks.timerInput.seconds')}
        placeholder="00"
        className={`doc-media-time-input doc-media-time-input--seconds`}
        style={{width: `2.7ch`}}
        ref={secRef}
        data-testid="doc-media-time-picker-seconds" />
    </Box>
  );
};
